/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Card, Table } from "core/components";
import { Option } from "core/types";
import formatter from "core/formatter";

type ProjectParticularsProps = {
  project: ProjectParticularsData;
  inventory_items: Array<Record<string, boolean>>;
  particular_types: Array<Option>;
};

const ProjectParticulars = ({ project, inventory_items, particular_types }: ProjectParticularsProps): JSX.Element => {
  const projectTaskParticulars = project?.project_tasks_attributes[0]?.project_task_items_attributes || [];
  const projectParticulars = projectTaskParticulars.map((projectTaskParticular) => {
    return {
      id: projectTaskParticular.id,
      item: inventory_items.find((inventory_item) => inventory_item.id == projectTaskParticular.item_id),
      budgeted_quantity: projectTaskParticular.budgeted_quantity,
      budgeted_unit_rate: projectTaskParticular.budgeted_unit_rate,
      variant: particular_types.find((particular_type) => particular_type.value == projectTaskParticular.variant)
    };
  });

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        Header: "Particular Type",
        accessor: "variant.label",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Inventory ID",
        accessor: "item.code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Inventory",
        accessor: "item.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "item.unit_measure.code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Budgeted Quantity",
        accessor: "budgeted_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Budgeted Unit Rate",
        accessor: "budgeted_unit_rate",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{formatter.format(props.value)}</p>
            </div>
          );
        }
      }
    ],
    []
  );

  return (
    <React.StrictMode>
      <Card.Body>
        <Container fluid={true}>
          <Row>
            <Col>
              <h5 className="mb-4">Project Particulars</h5>
              <Table columns={columns} data={projectParticulars} />
            </Col>
          </Row>
        </Container>
      </Card.Body>
    </React.StrictMode>
  );
};

export default ProjectParticulars;
